
import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import { Award, TrendingUp, Users, BookOpen, Calendar, Target } from 'lucide-react';

interface Certificate {
  id: string;
  user_id: string;
  user_name: string;
  course_id: string;
  course_title: string;
  certificate_number: string;
  issue_date: string;
  verification_hash: string;
  is_verified: boolean;
  metadata: any;
}

interface StaffCertificateAnalyticsProps {
  certificates: Certificate[];
}

const StaffCertificateAnalytics: React.FC<StaffCertificateAnalyticsProps> = ({ certificates }) => {
  const analytics = useMemo(() => {
    // Certificate issuance by month
    const monthlyIssuance = certificates.reduce((acc, cert) => {
      const month = new Date(cert.issue_date).toLocaleString('default', { month: 'short', year: '2-digit' });
      acc[month] = (acc[month] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const monthlyData = Object.entries(monthlyIssuance).map(([month, count]) => ({
      month,
      certificates: count
    }));

    // Certificates by course
    const courseDistribution = certificates.reduce((acc, cert) => {
      acc[cert.course_title] = (acc[cert.course_title] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const courseData = Object.entries(courseDistribution).map(([course, count]) => ({
      name: course,
      value: count,
      color: `hsl(${Math.random() * 360}, 70%, 50%)`
    }));

    // Grade distribution
    const gradeRanges = certificates.reduce((acc, cert) => {
      const grade = cert.metadata?.grade || 0;
      if (grade >= 90) acc['90-100%']++;
      else if (grade >= 80) acc['80-89%']++;
      else if (grade >= 70) acc['70-79%']++;
      else acc['Below 70%']++;
      return acc;
    }, { '90-100%': 0, '80-89%': 0, '70-79%': 0, 'Below 70%': 0 });

    const gradeData = Object.entries(gradeRanges).map(([range, count]) => ({
      range,
      count
    }));

    // Calculate averages
    const averageGrade = certificates.reduce((sum, cert) => sum + (cert.metadata?.grade || 0), 0) / certificates.length || 0;
    const averageCompletionTime = certificates.reduce((sum, cert) => sum + (cert.metadata?.completion_time || 0), 0) / certificates.length || 0;
    const verificationRate = (certificates.filter(cert => cert.is_verified).length / certificates.length) * 100 || 0;

    return {
      monthlyData,
      courseData,
      gradeData,
      averageGrade,
      averageCompletionTime,
      verificationRate,
      totalCertificates: certificates.length,
      uniqueUsers: new Set(certificates.map(cert => cert.user_id)).size,
      uniqueCourses: new Set(certificates.map(cert => cert.course_id)).size
    };
  }, [certificates]);

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

  return (
    <div className="space-y-6">
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Certificates</p>
                <p className="text-2xl font-bold">{analytics.totalCertificates}</p>
              </div>
              <Award className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Unique Users</p>
                <p className="text-2xl font-bold">{analytics.uniqueUsers}</p>
              </div>
              <Users className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Courses Completed</p>
                <p className="text-2xl font-bold">{analytics.uniqueCourses}</p>
              </div>
              <BookOpen className="h-8 w-8 text-purple-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Avg. Grade</p>
                <p className="text-2xl font-bold">{analytics.averageGrade.toFixed(1)}%</p>
              </div>
              <Target className="h-8 w-8 text-orange-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Performance Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-green-600">{analytics.verificationRate.toFixed(1)}%</div>
            <div className="text-sm text-muted-foreground">Verification Rate</div>
            <Badge variant="default" className="mt-2">Excellent</Badge>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-blue-600">{analytics.averageCompletionTime.toFixed(1)}d</div>
            <div className="text-sm text-muted-foreground">Avg. Completion Time</div>
            <Badge variant="secondary" className="mt-2">Good Pace</Badge>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-purple-600">
              {analytics.totalCertificates > 0 ? (analytics.totalCertificates / analytics.uniqueUsers).toFixed(1) : 0}
            </div>
            <div className="text-sm text-muted-foreground">Certificates per User</div>
            <Badge variant="outline" className="mt-2">Active Learners</Badge>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Issuance Trend */}
        <Card>
          <CardHeader>
            <CardTitle>Certificate Issuance Trend</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={analytics.monthlyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Line type="monotone" dataKey="certificates" stroke="#3b82f6" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Course Distribution */}
        <Card>
          <CardHeader>
            <CardTitle>Certificates by Course</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={analytics.courseData}
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                  label={({ name, value }) => `${name}: ${value}`}
                >
                  {analytics.courseData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Grade Distribution */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Grade Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={analytics.gradeData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="range" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="count" fill="#10b981" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Insights and Recommendations */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Insights & Recommendations
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <h4 className="font-medium">Performance Insights</h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Average grade of {analytics.averageGrade.toFixed(1)}% indicates {analytics.averageGrade >= 85 ? 'excellent' : analytics.averageGrade >= 75 ? 'good' : 'needs improvement'} performance</li>
                <li>• {analytics.verificationRate.toFixed(1)}% verification rate shows {analytics.verificationRate >= 90 ? 'strong' : 'moderate'} certificate integrity</li>
                <li>• {analytics.uniqueUsers} users have earned certificates across {analytics.uniqueCourses} courses</li>
              </ul>
            </div>
            <div className="space-y-2">
              <h4 className="font-medium">Recommendations</h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• {analytics.averageGrade < 80 ? 'Consider additional support for struggling learners' : 'Maintain current teaching quality'}</li>
                <li>• {analytics.verificationRate < 95 ? 'Review certificate verification process' : 'Certificate verification process is working well'}</li>
                <li>• Monitor course completion trends for resource planning</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default StaffCertificateAnalytics;
